/**
 * This slice is responsible for loading and storing the data.
 * It does not handle the UI state.
 *
 * This is for admin users only. The standard use case / data model
 * assumes a user is loading and accessing data here that they are
 * authorized to access. For regular users, see the map3Slice.ts file.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { UserProfile1, SignUpLink1, MilestoneMap1, Objective1 } from '../types';
import { doc, getDoc, collection, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  updateUser as updateUserCore,
  updateMilestoneMap as updateMilestoneMapCore,
  updateObjective as updateObjectiveCore,
  deleteObjective as deleteObjectiveCore,
  createSignUpLink as createSignUpLinkCore,
  UpdateData,
  processOperationQueue,
} from './map3PendingSlice';
import { AppDispatch, RootState } from './store';
import { upsertItem } from '../utils/upsertItem';

type AdminState = {
  userProfiles: UserProfile1[];
  milestoneMaps: MilestoneMap1[];
  objectives: Objective1[];
  isLoading: boolean;
  error: string | null;
  signupLinks: SignUpLink1[];
};

const initialState: AdminState = {
  userProfiles: [],
  milestoneMaps: [],
  objectives: [],
  isLoading: false,
  error: null,
  signupLinks: [],
};

const verifyAdminAccess = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const userProfileDocRef = doc(db, 'userProfiles', currentUser.uid);
  const userProfileDocSnap = await getDoc(userProfileDocRef);

  if (!userProfileDocSnap.exists()) {
    throw new Error('User profile document not found');
  }

  const userProfileData = userProfileDocSnap.data() as UserProfile1;
  if (userProfileData?.accountType !== 'alpha_admin') {
    throw new Error('Access denied: User is not an admin');
  }
};

export const loadUserProfiles = createAsyncThunk('admin1/loadUserProfiles', async () => {
  try {
    await verifyAdminAccess();
    const querySnapshot = await getDocs(collection(db, 'userProfiles'));
    const userProfiles: UserProfile1[] = [];
    querySnapshot.forEach((doc) => {
      userProfiles.push({ uid: doc.id, ...doc.data() } as UserProfile1);
    });
    return userProfiles;
  } catch (error) {
    console.error('Error loading user profiles:', error);
    throw error;
  }
});

export const updateUserProfile = createAsyncThunk<
  { uid: string; updates: Partial<UserProfile1> },
  { uid: string; updates: Partial<UserProfile1> },
  ThunkAPI
>('admin1/updateUserProfile', async (payload, { dispatch }) => {
  try {
    await verifyAdminAccess();
    const { uid, updates } = payload;
    return await dispatch(updateUserCore({ uid, updates })).unwrap();
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
});

/**
 * Delete a user profile from Firestore directly.
 * This is an admin-only function that provides direct database access
 * for deleting user profiles without going through map3PendingSlice.
 *
 * @see docs/work_in_progress/users-to-userProfiles/ADR.AdminUserProfileDeletion.md
 */
export const deleteUserProfile = createAsyncThunk<string, string, ThunkAPI>(
  'admin1/deleteUserProfile',
  async (uid, { getState }) => {
    try {
      // Verify admin access
      await verifyAdminAccess();

      // Delete from Firestore
      const docRef = doc(db, 'userProfiles', uid);
      await deleteDoc(docRef);

      console.log(`Admin deleted user profile ${uid}`);
      return uid;
    } catch (error) {
      console.error('Error deleting user profile:', error);
      throw error;
    }
  },
);

export const loadMilestoneMaps = createAsyncThunk('admin1/loadMilestoneMaps', async () => {
  try {
    await verifyAdminAccess();
    const querySnapshot = await getDocs(collection(db, 'milestoneMaps'));
    const maps: MilestoneMap1[] = [];
    querySnapshot.forEach((doc) => {
      maps.push({ uuid: doc.id, ...doc.data() } as MilestoneMap1);
    });
    return maps;
  } catch (error) {
    console.error('Error in loadMilestoneMaps:', error);
    throw error;
  }
});

export const loadObjectives = createAsyncThunk('admin1/loadObjectives', async (_, { dispatch }) => {
  try {
    await verifyAdminAccess();
    const querySnapshot = await getDocs(collection(db, 'objectives'));
    const objectives: Objective1[] = [];
    querySnapshot.forEach((doc) => {
      objectives.push({ uuid: doc.id, ...doc.data() } as Objective1);
    });
    return objectives;
  } catch (error) {
    console.error('Error loading objectives:', error);
    throw error;
  }
});

export const loadSignupLinks = createAsyncThunk('admin1/loadSignupLinks', async () => {
  const querySnapshot = await getDocs(collection(db, 'signUpLinks'));
  return querySnapshot.docs.map((doc) => doc.data() as SignUpLink1);
});

export const createSignupLink = createAsyncThunk<
  SignUpLink1,
  {
    toEmail: string;
    accountType: 'alpha_guest' | 'alpha_member';
    expirationDays: number;
  },
  ThunkAPI
>('admin1/createSignupLink', async (data, { dispatch }) => {
  try {
    await verifyAdminAccess();

    console.log('Admin creating signup link for:', data.toEmail);

    // Simply call the core implementation from map3PendingSlice
    const result = await dispatch(
      createSignUpLinkCore({
        toEmail: data.toEmail,
        accountType: data.accountType,
        expirationDays: data.expirationDays,
        // Optionally add admin user ID as creator
        createdBy: auth.currentUser?.uid,
      }),
    ).unwrap();

    // Ensure the operation queue is processed immediately.
    // Note: This should be unnecessary on pages where SaveAndRefresh
    // is present. But it _is_ necessary where SaveAndRefresh is not
    // automatically processing the queue in the background.
    await dispatch(processOperationQueue());

    console.log('Signup link created successfully from admin:', result.signUpCode);

    return result;
  } catch (error) {
    console.error('Error creating signup link:', error);
    throw error;
  }
});

// Add ThunkAPI type to fix dispatch type issues
type ThunkAPI = {
  dispatch: AppDispatch;
  state: RootState;
};

export const updateObjective = createAsyncThunk<
  UpdateData<Objective1>,
  UpdateData<Objective1>,
  ThunkAPI
>('admin1/updateObjective', async (payload, { dispatch }) => {
  try {
    await verifyAdminAccess();
    const { uuid, updates } = payload;
    return await dispatch(updateObjectiveCore({ uuid, updates })).unwrap();
  } catch (error) {
    console.error('Error updating objective:', error);
    throw error;
  }
});

export const deleteObjective = createAsyncThunk<string, string, ThunkAPI>(
  'admin1/deleteObjective',
  async (uuid, { dispatch }) => {
    try {
      await verifyAdminAccess();
      return await dispatch(deleteObjectiveCore(uuid)).unwrap();
    } catch (error) {
      console.error('Error deleting objective:', error);
      throw error;
    }
  },
);

// Wrapper thunk that adds admin verification
export const updateMilestoneMap = createAsyncThunk<
  UpdateData<MilestoneMap1>,
  UpdateData<MilestoneMap1>,
  ThunkAPI
>('admin1/updateMilestoneMap', async (payload, { dispatch }) => {
  try {
    await verifyAdminAccess();
    const { uuid, updates } = payload;
    return await dispatch(updateMilestoneMapCore({ uuid, updates })).unwrap();
  } catch (error) {
    console.error('Error in admin updateMilestoneMap:', error);
    throw error;
  }
});

const admin1Slice = createSlice({
  name: 'admin1',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Load User Profiles
      .addCase(loadUserProfiles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadUserProfiles.fulfilled, (state, action) => {
        state.userProfiles = action.payload;
        state.isLoading = false;
      })
      .addCase(loadUserProfiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load user profiles';
      })
      // Update User Profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        const { uid, updates } = action.payload;
        state.userProfiles = state.userProfiles.map((userProfile) =>
          userProfile.uid === uid ? { ...userProfile, ...updates } : userProfile,
        );
      })
      // Delete User Profile (new direct implementation)
      .addCase(deleteUserProfile.fulfilled, (state, action) => {
        state.userProfiles = state.userProfiles.filter(
          (userProfile) => userProfile.uid !== action.payload,
        );
      })
      // Load Milestone Maps
      .addCase(loadMilestoneMaps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadMilestoneMaps.fulfilled, (state, action) => {
        state.milestoneMaps = action.payload;
        state.isLoading = false;
      })
      .addCase(loadMilestoneMaps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load milestone maps';
      })
      // Load Objectives
      .addCase(loadObjectives.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadObjectives.fulfilled, (state, action) => {
        state.objectives = action.payload;
        state.isLoading = false;
      })
      .addCase(loadObjectives.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load objectives';
      })
      // Update Objective
      .addCase(updateObjective.fulfilled, (state, action) => {
        const { uuid, updates } = action.payload;
        state.objectives = state.objectives.map((objective) =>
          objective.uuid === uuid ? { ...objective, ...updates } : objective,
        );
      })
      // Delete Objective
      .addCase(deleteObjective.fulfilled, (state, action) => {
        state.objectives = state.objectives.filter(
          (objective) => objective.uuid !== action.payload,
        );
      })
      // Update Milestone Map
      .addCase(updateMilestoneMap.fulfilled, (state, action) => {
        const { uuid, updates } = action.payload;
        const m = state.milestoneMaps.find((m) => m.uuid === uuid);
        if (!m) {
          console.error('Milestone map not found');
          return;
        }
        const updatedMap = {
          ...m,
          ...updates,
          // Ensure createdBy remains a string
          createdBy: typeof updates.createdBy === 'string' ? updates.createdBy : m.createdBy,
        };
        state.milestoneMaps = upsertItem(
          state.milestoneMaps,
          updatedMap,
          (map) => map.uuid === uuid,
        );
      })
      // Load Signup Links
      .addCase(loadSignupLinks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadSignupLinks.fulfilled, (state, action) => {
        state.signupLinks = action.payload;
        state.isLoading = false;
      })
      .addCase(loadSignupLinks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load signup links';
      })
      // Create Signup Link
      .addCase(createSignupLink.fulfilled, (state, action) => {
        state.signupLinks.push(action.payload);
      });
  },
});

export default admin1Slice.reducer;
